import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Loader2, Building, Briefcase, Phone, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';

// Terms and Conditions content
const TERMS_AND_CONDITIONS = `Terms and Conditions

Last Updated: 03/16/26

Welcome to OPERATR Dashboard ("Platform", "Service", "Application", "we", "our", or "us"). These Terms and Conditions govern your use of our software platform, website, and services.

By creating an account, accessing, or using the Platform, you agree to comply with and be bound by these Terms.

If you do not agree to these Terms, you may not access or use the Platform.

⸻

1. Eligibility

By creating an account or using the Platform, you represent that:

• you are legally capable of entering into a binding agreement under applicable laws
• the information you provide is accurate and current
• you will maintain the security of your account credentials

If you are using the Platform on behalf of a company, organization, or other entity, you represent that you have the authority to bind that entity to these Terms.

You are responsible for all activity that occurs under your account.

⸻

2. Description of the Service

OPERATR Dashboard is a software platform that provides tools for workflow management, automation, integrations, analytics, communications, and productivity systems.

The platform may connect with third-party services, including but not limited to:

• cloud storage services
• financial software
• communication platforms
• streaming services
• productivity tools
• delivery services
• artificial intelligence tools
• external APIs

We reserve the right to modify, improve, or discontinue features at any time.

⸻

3. User Accounts

To use certain features of the Platform, you must create an account.

You agree to:

• provide accurate information
• keep your login credentials secure
• notify us immediately of unauthorized access
• not share your account with unauthorized users

We reserve the right to suspend or terminate accounts that violate these Terms.

⸻

4. Acceptable Use

You agree not to use the Platform for:

• illegal activities
• distributing copyrighted material without authorization
• hacking or security violations
• transmitting malware or malicious code
• harassment or abusive behavior
• unauthorized access to third-party systems

You may not attempt to:

• reverse engineer the software
• exploit vulnerabilities
• resell access without authorization
• scrape or copy platform data

Violation of these rules may result in immediate account suspension.

⸻

5. Third-Party Integrations

The Platform may integrate with services operated by third parties.

Examples include:

• streaming platforms
• communication tools
• payment processors
• delivery services
• cloud storage providers

We are not responsible for the availability, reliability, or policies of third-party services.

Your use of those services is subject to their own terms.

⸻

6. Subscription & Payments

Certain features require a paid subscription or license.

By subscribing, you agree to:

• pay all applicable fees
• maintain valid payment information
• allow automatic billing where applicable

Subscriptions may be billed:

• monthly
• annually
• per-license
• enterprise agreements

Fees are non-refundable unless required by law.

We reserve the right to change pricing with reasonable notice.

⸻

7. Intellectual Property

All software, design, features, and functionality of the Platform are owned by OPERATR Dashboard and protected by intellectual property laws.

Users are granted a limited, non-exclusive license to use the Platform.

You may not:

• copy the software
• distribute the platform
• sell or sublicense the software
• reproduce proprietary components

⸻

8. User Data

Users retain ownership of their data uploaded to the Platform.

However, by using the service, you grant us permission to:

• process data necessary to operate the platform
• store data securely
• improve services and performance

We take reasonable measures to protect user data, but no system can guarantee absolute security.

⸻

9. Data Security

We implement industry standard security practices including:

• encryption
• access controls
• secure cloud infrastructure
• monitoring and logging

Users are responsible for:

• protecting their credentials
• ensuring authorized access to their accounts

⸻

10. Service Availability

We strive to maintain high uptime but do not guarantee uninterrupted service.

The Platform may experience downtime due to:

• maintenance
• system upgrades
• security updates
• external service outages

We are not liable for temporary service interruptions.

⸻

11. Limitation of Liability

To the fullest extent permitted by law, OPERATR Dashboard shall not be liable for:

• indirect damages
• lost profits
• data loss
• business interruption
• third-party service failures

Total liability shall not exceed the amount paid by the user in the preceding 12 months.

⸻

12. Termination

We may suspend or terminate accounts if users:

• violate these Terms
• misuse the platform
• engage in illegal activity

Users may cancel their account at any time.

Upon termination, access to the Platform may be revoked.

⸻

13. Changes to Terms

We may update these Terms periodically.

If significant changes occur, users will be notified via:

• email
• platform notification
• website update

Continued use of the Platform constitutes acceptance of updated Terms.

⸻

14. Governing Law

These Terms shall be governed by the laws of the State of Texas, United States, without regard to conflict of law principles.

⸻

15. Contact Information

For questions regarding these Terms, contact:

OPERATR Dashboard
Email: creatrhq@gmail.com
Website: www.creatrverse.com`;

// Terms Modal Component
const TermsModal = ({ isOpen, onClose, onAccept }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <FileText size={20} className="text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Terms & Conditions</h2>
              <p className="text-sm text-gray-400">Please read carefully before continuing</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose prose-invert prose-sm max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-gray-300 text-sm leading-relaxed">
              {TERMS_AND_CONDITIONS}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 bg-gray-900/50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              onClick={onAccept}
              className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-lg transition-all"
            >
              I Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Smart API URL detection - same logic as UserAuthContext
const getApiUrl = () => {
  const currentOrigin = window.location.origin;
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If we're on a custom domain (not preview.emergentagent.com), use the current origin
  if (!currentOrigin.includes('preview.emergentagent.com')) {
    return currentOrigin;
  }
  
  // For preview environments, use the env variable or fallback to origin
  return envUrl || currentOrigin;
};

const API_URL = getApiUrl();

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loginWithGoogle, isAuthenticated, user } = useAuth();
  
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');

  // Inject the UnicornStudio background script — only on this page, only once.
  useEffect(() => {
    const init = () => {
      if (window.UnicornStudio && typeof window.UnicornStudio.init === 'function') {
        try { window.UnicornStudio.init(); } catch { /* swallow */ }
      }
    };
    if (window.UnicornStudio && window.UnicornStudio.init) {
      init();
      return undefined;
    }
    window.UnicornStudio = window.UnicornStudio || { isInitialized: false };
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v2.2.0/dist/unicornStudio.umd.js';
    s.async = true;
    s.onload = init;
    document.head.appendChild(s);
    return () => {
      // Don't remove the script — UnicornStudio caches globally and re-init
      // is cheap. Removing it can mid-flight cancel the canvas WebGL context.
    };
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Admin users bypass subscription requirement
      // Check both boolean true and string 'true' for robustness
      const isAdmin = user.is_admin === true || user.is_admin === 'true';
      const subscriptionStatus = user.subscription_status;
      
      console.log('[AuthPage] Already authenticated redirect - isAdmin:', isAdmin, '| subscription:', subscriptionStatus);
      
      if (isAdmin) {
        // Admin users go straight to dashboard or onboarding
        if (user.onboarding_completed) {
          navigate('/dashboard');
        } else {
          navigate('/onboarding');
        }
      } else if (!subscriptionStatus || subscriptionStatus === 'none') {
        // No subscription - redirect to pricing
        navigate('/pricing');
      } else if (user.onboarding_completed) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Handle Google OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    
    if (code) {
      handleGoogleCallback(code);
    }
  }, [location]);

  const handleGoogleCallback = async (code) => {
    setGoogleLoading(true);
    try {
      const redirectUri = `${window.location.origin}/auth`;
      const result = await loginWithGoogle(code, redirectUri);
      
      if (result.success) {
        toast.success('Welcome! Signed in with Google');
        // Admin users bypass subscription requirement
        // Check both boolean true and string 'true' for robustness
        const isAdmin = result.user.is_admin === true || result.user.is_admin === 'true';
        const subscriptionStatus = result.user.subscription_status;
        
        if (isAdmin) {
          // Admin users go straight to dashboard or onboarding
          if (result.user.onboarding_completed) {
            navigate('/dashboard');
          } else {
            navigate('/onboarding');
          }
        } else if (!subscriptionStatus || subscriptionStatus === 'none') {
          // No subscription - redirect to pricing
          navigate('/pricing');
        } else if (result.user.onboarding_completed) {
          navigate('/dashboard');
        } else {
          navigate('/onboarding');
        }
      } else {
        toast.error(result.error || 'Google sign-in failed');
      }
    } catch (error) {
      toast.error('Google sign-in failed');
    } finally {
      setGoogleLoading(false);
      // Clear URL params
      window.history.replaceState({}, document.title, '/auth');
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const redirectUri = `${window.location.origin}/auth`;
      console.log('Initiating Google login with redirect:', redirectUri);
      
      const response = await fetch(`${API_URL}/api/user-auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();
      
      console.log('Google URL response:', data);
      
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Google sign-in not available');
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Failed to initiate Google sign-in. Please try email login.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (mode === 'register') {
      if (!acceptedTerms) {
        toast.error('Please accept the Terms & Conditions to create an account');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }

    setLoading(true);

    try {
      let result;
      
      if (mode === 'login') {
        result = await login(email, password);
      } else {
        result = await register({
          email,
          password,
          name,
          company: company || null,
          role: role || null,
          phone: phone || null
        });
      }

      if (result.success) {
        if (mode === 'register') {
          // New users go to pricing first
          toast.success('Account created! Please select a plan to continue.');
          navigate('/pricing');
        } else {
          // Existing users
          toast.success('Welcome back!');
          // Admin users bypass subscription requirement
          // Check both boolean true and string 'true' for robustness
          const isAdmin = result.user.is_admin === true || result.user.is_admin === 'true';
          const subscriptionStatus = result.user.subscription_status;
          
          console.log('[AuthPage] Login redirect - isAdmin:', isAdmin, '| subscription:', subscriptionStatus, '| onboarding:', result.user.onboarding_completed);
          
          if (isAdmin) {
            // Admin users go straight to dashboard or onboarding
            if (result.user.onboarding_completed) {
              navigate('/dashboard');
            } else {
              navigate('/onboarding');
            }
          } else if (!subscriptionStatus || subscriptionStatus === 'none') {
            // No subscription - redirect to pricing
            navigate('/pricing');
          } else if (result.user.onboarding_completed) {
            navigate('/dashboard');
          } else {
            navigate('/onboarding');
          }
        }
      } else {
        toast.error(result.error || 'Authentication failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (googleLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-white text-lg">Signing in with Google...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col xl:flex-row">
      {/* Left — UnicornStudio animated background (hidden on phone + iPad, shown on desktop xl+) */}
      <div className="relative hidden xl:block xl:flex-[1.4] overflow-visible">
        <div
          data-us-project="Hg0XRWoIgweWUcSTTO9Y"
          className="absolute inset-y-0 left-0 h-full"
          style={{ width: '120%', height: '100%', transform: 'translateX(5%)' }}
        />
      </div>

      {/* Right — auth form */}
      <div className="flex-1 flex items-center justify-center p-6 xl:justify-start xl:pl-72 xl:pr-12 bg-transparent relative">
        <div className="w-full max-w-md relative z-10">
          {/* Heading */}
          <div className="mb-8">
            <h2
              className={`text-white font-semibold tracking-[0.18em] uppercase ${mode === 'login' ? 'text-3xl' : 'text-2xl'} whitespace-nowrap`}
              style={{ fontFamily: "'Michroma', 'Orbitron', sans-serif" }}
            >
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </h2>
            <p className="text-gray-400 text-sm mt-2 leading-relaxed">
              {mode === 'login'
                ? 'Welcome back to your operating system.'
                : ''}
            </p>
          </div>

          {/* Auth Card — open form, no glass blur, on solid black */}
          <div className="space-y-4">
          {/* Tabs */}
          <div className="flex mb-8 bg-white rounded-lg p-1 border border-white/30">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all ${
                mode === 'login'
                  ? 'bg-black text-white'
                  : 'text-black hover:bg-gray-200'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all ${
                mode === 'register'
                  ? 'bg-black text-white'
                  : 'text-black hover:bg-gray-200'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 bg-white hover:bg-gray-100 text-black rounded-lg font-medium transition-colors mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-700"></div>
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-gray-700"></div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name *</label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-black border border-white/30 rounded-lg text-white placeholder-gray-500 transition-all duration-200 hover:border-white/60 hover:shadow-[0_0_18px_rgba(255,255,255,0.18)] focus:outline-none focus:border-white focus:shadow-[0_0_22px_rgba(255,255,255,0.28)]"
                  />
                </div>
              </div>
            )}

            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Your Role *</label>
                <div className="relative">
                  <Briefcase size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g., CEO, Manager, Developer"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-black border border-white/30 rounded-lg text-white placeholder-gray-500 transition-all duration-200 hover:border-white/60 hover:shadow-[0_0_18px_rgba(255,255,255,0.18)] focus:outline-none focus:border-white focus:shadow-[0_0_22px_rgba(255,255,255,0.28)]"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-black border border-white/30 rounded-lg text-white placeholder-gray-500 transition-all duration-200 hover:border-white/60 hover:shadow-[0_0_18px_rgba(255,255,255,0.18)] focus:outline-none focus:border-white focus:shadow-[0_0_22px_rgba(255,255,255,0.28)]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Password *</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full pl-10 pr-12 py-3 bg-black border border-white/30 rounded-lg text-white placeholder-gray-500 transition-all duration-200 hover:border-white/60 hover:shadow-[0_0_18px_rgba(255,255,255,0.18)] focus:outline-none focus:border-white focus:shadow-[0_0_22px_rgba(255,255,255,0.28)]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm Password *</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full pl-10 pr-4 py-3 bg-black border border-white/30 rounded-lg text-white placeholder-gray-500 transition-all duration-200 hover:border-white/60 hover:shadow-[0_0_18px_rgba(255,255,255,0.18)] focus:outline-none focus:border-white focus:shadow-[0_0_22px_rgba(255,255,255,0.28)]"
                    />
                  </div>
                </div>

              </>
            )}

            {/* Terms & Conditions Checkbox - Only for registration */}
            {mode === 'register' && (
              <div className="flex items-start gap-3 pt-4">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500 focus:ring-offset-gray-900 cursor-pointer"
                />
                <label htmlFor="terms-checkbox" className="text-sm text-gray-400">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-purple-400 hover:text-purple-300 underline"
                  >
                    Terms & Conditions
                  </button>
                  {' '}*
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (mode === 'register' && !acceptedTerms)}
              className="w-full py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-6 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                mode === 'login' ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {mode === 'login' && (
            <p className="text-center text-white text-sm mt-4">
              <button className="text-white hover:text-gray-300">
                Forgot password?
              </button>
            </p>
          )}
        </div>

        <p className="text-center text-white text-sm mt-6">
          By continuing, you agree to our{' '}
          <button 
            onClick={() => setShowTermsModal(true)}
            className="text-white hover:text-gray-300 underline"
          >
            Terms & Conditions
          </button>
        </p>
      </div>
      </div>

      {/* Terms & Conditions Modal */}
      <TermsModal 
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setAcceptedTerms(true);
          setShowTermsModal(false);
          toast.success('Terms & Conditions accepted');
        }}
      />
    </div>
  );
};

export default AuthPage;
